import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Navigation, Info as InfoIcon, Video, ExternalLink } from "lucide-react";
import { toEmbedUrl } from "@/lib/video";

export default function Directions() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data));
  }, []);

  const mapAddress = info?.address || "Musterdorf";
  const mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapAddress)}&output=embed`;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="directions-page">
      <div className="text-center mb-12">
        <p className="label-eyebrow mb-3">So findest du uns</p>
        <h1 className="font-serif text-5xl lg:text-6xl tracking-tight">Anfahrt</h1>
      </div>

      <div className="grid lg:grid-cols-5 gap-8 mb-12">
        <div className="lg:col-span-3 rounded-2xl overflow-hidden border border-[#E5DFD3] bg-white aspect-[4/3]">
          <iframe
            title="Anfahrtskarte"
            src={mapUrl}
            className="w-full h-full"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="map-iframe"
          />
        </div>

        <div className="lg:col-span-2 space-y-5">
          {info && (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapAddress)}`}
              target="_blank"
              rel="noreferrer"
              className="block bg-[#48634A] hover:bg-[#364A38] transition-colors rounded-2xl p-6 text-[#FDFBF7]"
              data-testid="navigate-btn"
            >
              <div className="flex items-center gap-2 mb-2 opacity-80">
                <Navigation className="w-4 h-4" />
                <span className="label-eyebrow text-[#FDFBF7]">Route starten</span>
              </div>
              <p className="font-serif text-2xl tracking-tight">{info.location}</p>
              <p className="text-sm opacity-90 mt-1">{info.address}</p>
            </a>
          )}
        </div>
      </div>

      {/* Langer Infotext zur Anreise */}
      {info?.directions_text && (
        <section className="bg-white border border-[#E5DFD3] rounded-2xl p-6 lg:p-8 mb-12 shadow-[0_8px_30px_-22px_rgba(72,99,74,0.3)]" data-testid="directions-text">
          <div className="flex items-center gap-2 text-[#48634A] mb-4">
            <InfoIcon className="w-4 h-4" />
            <span className="label-eyebrow">Infos zur Anreise</span>
          </div>
          <div className="font-serif text-lg lg:text-xl text-[#1A1C19] leading-relaxed whitespace-pre-line">
            {info.directions_text}
          </div>
        </section>
      )}

      {/* Eingebettetes Video */}
      {info?.directions_video_url && <DirectionsVideo url={info.directions_video_url} title={info.directions_video_title} />}
    </div>
  );
}

function DirectionsVideo({ url, title }) {
  const embed = toEmbedUrl(url);
  if (!embed) return null;
  return (
    <section className="bg-white border border-[#E5DFD3] rounded-2xl p-6 lg:p-8 shadow-[0_8px_30px_-22px_rgba(72,99,74,0.3)]" data-testid="directions-video">
      <div className="flex items-center gap-2 text-[#3F6E96] mb-4">
        <Video className="w-4 h-4" />
        <span className="label-eyebrow" style={{ color: "#3F6E96" }}>{title || "Video zur Anreise"}</span>
      </div>
      {embed.type === "iframe" && (
        <div className="rounded-xl overflow-hidden border border-[#E5DFD3] aspect-video bg-black">
          <iframe
            src={embed.src}
            title={title || "Video zur Anreise"}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            className="w-full h-full"
            style={{ border: 0 }}
          />
        </div>
      )}
      {embed.type === "video" && (
        <video controls src={embed.src} className="w-full rounded-xl border border-[#E5DFD3]" />
      )}
      {embed.type === "link" && (
        <a href={embed.external} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#3F6E96] hover:text-[#2c5274]">
          <ExternalLink className="w-4 h-4" /> Video öffnen
        </a>
      )}
    </section>
  );
}
